import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Switch,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { LoadingButton } from '@mui/lab';

import API from 'src/data/api';
import CustomModal from '../../custom/CustomModal';
import { isNullValue, validateForm } from 'src/utils/formUtil';
import CustomTextField from 'src/components/custom/CustomTextField';
import { MAX_FILE_SIZE_MB } from '../../../utils/constants';
import FileDragzone from 'src/components/ui/FileDragzone';
import palette from 'src/theme/palette';
import { fNumber } from 'src/utils/formatNumber';
import CustomDateTimePicker from 'src/components/custom/CustomDateTimePicker';
import moment from 'moment';

const initDelivery = {
  volume: { value: "", required: true, error: false, errorMessage: 'Please specify delivered volume' },
  date: { value: null, required: true, error: false, errorMessage: 'Please specify delivery date' },
  document: {
    fileObjects: [],
    filePreviewUrl: null,
    isFilePreview: false,
    isPdfFile: false,
    modalStyle: {},
    type: { value: 'DELIVERY_NOTE', required: false, error: false, errorMessage: 'Please specify a document type', },
    details: null,
  },
  note: { value: "", required: false, error: false, errorMessage: '' },
  upload: { value: false, required: true, error: false, errorMessage: '' },
}

export const DepotDeliveryConfirmation = ({
  truckAssignment,
  openModal,
  handleCloseModal,
  handleSubmitCallback,
}) => {

  const [submitted, setSubmitted] = useState(false);
  const [delivery, setDelivery] = useState({ ...initDelivery });

  useEffect(() => {
    console.log(truckAssignment);
  }, [openModal, truckAssignment]);

  const handleSubmitDeliveryConfirmation = async () => {

    if (isNullValue(truckAssignment?.suborder_id) || !validateForm(setDelivery, delivery)) {
      console.log("opps, validaion error", delivery)
      return;
    }

    const endPoint = `inventory/logistics/confirm_depot_delivery`;
    const delivery_date = moment(delivery.date.value).format("yyyy-MM-DDThh:mm:ss");
    const paylod = {
      delivery_date: delivery_date,
      delivered_volume: Number(delivery.volume.value),
      depot_id: truckAssignment?.depot_id,
      product_id: truckAssignment?.product_id,
      product_movement_id: truckAssignment?.product_movement_id,
      supplier_suborder_id: truckAssignment?.supplier_suborder_id,
      tank_id: truckAssignment?.tank_id,
    };

    setSubmitted(true);
    try {
      const res = await API.post(`${endPoint}`, paylod);
      if (res.status === 200 || res.status === 201) {
        handleResetAndClose();
        handleSubmitCallback();
      } else {
        console.log("Error:", res.data);
      }
    } catch (error) {
      console.error('Error uploading document:', error);
    }
    setSubmitted(false);
  }

  const handleResetAndClose = () => {
    setDelivery({ ...initDelivery });
    handleCloseModal();
  }

  const handleInputChange = (field, value, error = false, message = null) => {
    const _delivery = {
      ...delivery,
      [field]: {
        ...delivery[field],
        value: value,
        error: (isNullValue(value) && delivery[field]['required']) || error,
        errorMessage: message || delivery[field]['errorMessage'],
      },
    }
    setDelivery(_delivery);
  };

  const handleFileObjects = (files) => {
    const file = (files.length > 0) ? files[0] : null;
    const doc = {
      ...delivery.document,
      fileObjects: files,
      filePreviewUrl: (file ? URL.createObjectURL(file) : null),
      isPdfFile: (file?.type === 'application/pdf'),
    }
    handleInputChange('document', doc)
  };

  return (
    <>
      {truckAssignment && (
        <CustomModal
          openModal={openModal}
          handleCloseModal={handleResetAndClose}
          modalTitle={"Confirm Depot Delivery"}
          modalIcon='transport'
        >
          <Typography
            variant="inherit"
            sx={{ color: palette.grey[500] }}>
            <em>
              {`Expected ${truckAssignment.client_name} top up with  ${fNumber(truckAssignment.volume)} litres`}
            </em>
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <img style={{ height: 100, width: '60%' }}
              alt="Logistics"
              src="/assets/images/truck-tanker-icon.jpg"
            />
          </Box>

          <CustomTextField sx={{ my: 1 }}
            type="number"
            id="volume"
            variant="outlined"
            name='volume'
            label={`Volume`}
            value={delivery?.volume.value}
            onChange={(e) => {
              const value = e.target.value;
              const maxVolume = Math.ceil(truckAssignment?.volume * 1.15);
              const error = (value < 1 || value > maxVolume);
              handleInputChange('volume', value, error, "Please specify correct volume, not more than " + maxVolume);
            }}
            required={delivery?.volume.required}
            error={delivery?.volume.error}
            helperText={delivery?.volume.error && delivery?.volume.errorMessage}
          />

          <FormControl fullWidth required={delivery.date.required} error={delivery.date.error}>
            <CustomDateTimePicker sx={{ overflow: 'hidden', height: 50 }}
              clear
              label={"Delivery Date"}
              field={'date'}
              currentValue={delivery.date.value}
              handleChange={handleInputChange}
              setClear={()=>handleInputChange('date', null, false)}
              dateError={delivery.date.error && 'invalidDate'}
              required={delivery.date.required}
            />
            <FormHelperText id="delivery-date-err" sx={{color: 'red'}} >
              {delivery.date.error ? delivery.date.errorMessage : ''}
            </FormHelperText>
          </FormControl>

          <CustomTextField sx={{ my: 1 }}
            multiline
            id="note"
            variant="outlined"
            name='note'
            label={`Note`}
            onChange={(e) => handleInputChange("note", e.target.value)}
            value={delivery?.note.value}
            required={delivery?.note.required}
            error={delivery?.note.error}
            helperText={delivery?.note.error && delivery?.note.errorMessage}
          />

          {delivery.upload.value && (
            <FileDragzone
              acceptedTypes={{
                'application/pdf': ['.pdf'],
                'image/jpeg': ['.jpg', '.jpeg'],
                'image/png': ['.png',],
              }}
              fileObjects={delivery.document.fileObjects}
              handleFileObjects={handleFileObjects}
              filesLimit={1}
              maxFileSizeMB={MAX_FILE_SIZE_MB}
            />
          )}

          <Grid container alignItems={'center'} sx={{ mt: 1, }}>
            <Grid item size={{ xs: 8.5 }} sx={{ my: 'auto', display: 'flex', justifyContent: 'left' }} >
              <FormControl component="fieldset">
                <FormControlLabel
                  sx={{ margin: 0 }}
                  control={
                    <Switch
                      checked={delivery?.upload?.value}
                      onChange={(e) => handleInputChange("upload", e.target.checked)}
                      name="upload"
                    />
                  }
                  label="Upload Document? "
                  labelPlacement="start"
                />
              </FormControl>
            </Grid>
            <Grid item size={{ xs: 2 }} sx={{ my: 'auto', display: 'flex', alignContent: 'end' }}>
              <Button
                variant='contained'
                color="inherit"
                onClick={() => handleResetAndClose()}
              >
                Cancel
              </Button>

              <LoadingButton
                sx={{ ml: 2, }}
                variant="contained"
                loading={submitted}
                onClick={() => handleSubmitDeliveryConfirmation()}
              >
                Finish
              </LoadingButton>
            </Grid>
          </Grid>
        </CustomModal>
      )}
    </>
  )
}

DepotDeliveryConfirmation.propTypes = {

  /** Selected subOrder details */
  subOrder: PropTypes.object.isRequired,

  /** List of suitable clients */
  clients: PropTypes.array.isRequired,

  /** Flag to toggling openning of modal */
  openModal: PropTypes.bool,

  /** Callback function when closing modal */
  handleCloseModal: PropTypes.func.isRequired,

  /** Callback function after assignment of suborder to clients */
  handleSubmitCallback: PropTypes.func.isRequired,

}